// Intersection Observer Polyfill for IE-11 and Safari
import 'intersection-observer';
import debounce from 'lodash/debounce';
import range from 'lodash/range';
import { Directive, Input, ElementRef, AfterViewInit, OnDestroy, HostListener } from '@angular/core';

interface IPrimaryElementStyle {
  position: string;
  top: string;
  zIndex: number | string;
  marginTop: string;
  width: string;
  height: string;
}

@Directive({
  selector: '[stickyAdjacentChildren]',
})
export class StickyAdjacentChildrenDirective implements AfterViewInit, OnDestroy {
  @Input() public rootElement: HTMLElement;
  @Input() public stickyOffset: number = 0;
  private prevRootElementHeight: number;
  private primaryElement: HTMLElement;
  private adjacentSiblingElement: HTMLElement;
  private debouncedPrimaryElementPointerStyleReset: any;
  private primaryElementObserver: IntersectionObserver;
  private adjacentElementObserver: IntersectionObserver;
  private boundedWheelEvent: () => void;
  private debouncedRecalculateStickyElementTop: () => void;

  constructor(el: ElementRef) {
    this.primaryElement = el.nativeElement;

    this.debouncedPrimaryElementPointerStyleReset = debounce(() => {
      if (!this.primaryElement) return;
      const primaryElement: HTMLElement = this.primaryElement;
      primaryElement.style.pointerEvents = '';
    }, 250);
    this.boundedWheelEvent = this.onWheelEvent.bind(this);

    this.debouncedRecalculateStickyElementTop = debounce(this.recalculateStickyElementTop.bind(this), 250);
  }

  public ngAfterViewInit() {
    this.adjacentSiblingElement = (this.primaryElement as HTMLElement).nextElementSibling as HTMLElement;

    if (!this.checkIfStickinessIsNeeded) return;
    this.primaryElementObserver = this.initPrimaryElementObserver();
    this.adjacentElementObserver = this.initAdjacentElementObserver();
    document.addEventListener('wheel', this.boundedWheelEvent);
    this.prevRootElementHeight = this.rootElement.clientHeight - this.stickyOffset;
  }

  public ngOnDestroy() {
    document.removeEventListener('wheel', this.boundedWheelEvent);
    if (this.primaryElementObserver) this.primaryElementObserver.unobserve(this.rootElement);
    if (this.adjacentElementObserver) this.adjacentElementObserver.unobserve(this.rootElement);
  }

  @HostListener('window:resize')
  private onResize(): void {
    this.debouncedRecalculateStickyElementTop();
  }

  private recalculateStickyElementTop(): void {
    if (!this.checkIfStickinessIsNeeded || (this.prevRootElementHeight === this.rootElement.clientHeight)) return;
    this.prevRootElementHeight = this.rootElement.clientHeight;
    if (this.isPrimaryElementSticky) {
      const rootElementDimensions: ClientRect = this.rootElement.getBoundingClientRect();
      const topAnchor: number = rootElementDimensions.top;
      this.primaryElement.style.top = topAnchor + 'px';
    }
  }

  private initPrimaryElementObserver(): IntersectionObserver {
    const primaryElementThreshold: number[] = range(0, 1, 1 / this.primaryElement.clientTop);
    const primaryElementObserverOptions: IntersectionObserverInit = {
      root: this.rootElement,
      rootMargin: this.rootElement.style.margin || '0%',
      threshold: primaryElementThreshold,
    };
    const primaryElementObserver: IntersectionObserver = new IntersectionObserver(
      this.onPrimaryElementObservation.bind(this),
      primaryElementObserverOptions,
    );
    primaryElementObserver.observe(this.primaryElement);
    return primaryElementObserver;
  }

  private initAdjacentElementObserver(): IntersectionObserver {
    const adjacentElementThreshold: number[] = range(0, 1, 1 / this.adjacentSiblingElement.offsetHeight);
    const adjacentElementObserverOptions: IntersectionObserverInit = {
      root: this.rootElement,
      rootMargin: this.rootElement.style.margin || '0%',
      threshold: adjacentElementThreshold,
    };
    const adjacentElementObserver: IntersectionObserver = new IntersectionObserver(
      this.onAdjacentElementObservation.bind(this),
      adjacentElementObserverOptions,
    );
    adjacentElementObserver.observe(this.adjacentSiblingElement);
    return adjacentElementObserver;
  }

  private onPrimaryElementObservation(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry: IntersectionObserverEntry) => {
      const primaryElementDimensions: ClientRect = entry.boundingClientRect;
      const rootElementDimensions: ClientRect = entry.rootBounds;
      const topAnchor: number = rootElementDimensions.top + this.stickyOffset;

      // if block below handles a corner case where intersection observer api returns zero values
      if (primaryElementDimensions.top == 0 && primaryElementDimensions.bottom == 0) return;

      if (!this.isPrimaryElementSticky && !this.isPrimaryElementRelative && primaryElementDimensions.top <= topAnchor) {
        this.primaryElementStyle = {
          position: 'fixed',
          top: topAnchor + 'px',
          zIndex: 1,
          marginTop: '0',
          width: primaryElementDimensions.width + 'px',
          height: primaryElementDimensions.height + 'px',
        };
        this.adjacentSiblingElement.style.marginTop = primaryElementDimensions.height + 'px';
      }
    });
  }

  private onAdjacentElementObservation(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry: IntersectionObserverEntry) => {
      const adjacentSiblingElement = this.adjacentSiblingElement;
      const adjacentElementDimensions: ClientRect = entry.boundingClientRect;
      const rootElementDimensions: ClientRect = entry.rootBounds;
      const primaryElement = this.primaryElement;
      const primaryElementDimensions: ClientRect = primaryElement.getBoundingClientRect();
      const topAnchor: number = rootElementDimensions.top;

      // if block below handles a corner case where intersection observer api returns zero values
      if (adjacentElementDimensions.top == 0 && adjacentElementDimensions.bottom == 0) return;

      if (this.isPrimaryElementSticky && primaryElementDimensions.bottom <= adjacentElementDimensions.top) {
        this.primaryElementStyle = {
          position: '',
          top: '',
          zIndex: '',
          marginTop: '',
          width: '',
          height: '',
        };
        adjacentSiblingElement.style.marginTop = '';
      } else if (this.isPrimaryElementSticky && primaryElementDimensions.bottom >= adjacentElementDimensions.bottom) {
        this.primaryElementStyle = {
          position: 'relative',
          top: adjacentElementDimensions.height + 'px',
          zIndex: 1,
          marginTop: '0',
          width: primaryElementDimensions.width + 'px',
          height: primaryElementDimensions.height + 'px',
        };
        adjacentSiblingElement.style.marginTop = '';
      } else if (this.isPrimaryElementRelative && primaryElementDimensions.top >= topAnchor) {
        this.primaryElementStyle = {
          position: 'fixed',
          top: topAnchor + 'px',
          zIndex: 1,
          marginTop: '0',
          width: primaryElementDimensions.width + 'px',
          height: primaryElementDimensions.height + 'px',
        };
        adjacentSiblingElement.style.marginTop = primaryElementDimensions.height + 'px';
      }
    });
  }

  private onWheelEvent(): void {
    const primaryElement: HTMLElement = this.primaryElement;
    if (!primaryElement) return;
    if (primaryElement.style.pointerEvents === 'none') {
      this.debouncedPrimaryElementPointerStyleReset();
      return;
    }
    primaryElement.style.pointerEvents = 'none';
    this.debouncedPrimaryElementPointerStyleReset();
  }

  private get checkIfStickinessIsNeeded(): boolean {
    const rootHeight: number = this.rootElement.offsetHeight;
    const adjacentSiblingElementHeight: number = this.adjacentSiblingElement.offsetHeight;
    const primaryElementHeight: number = this.primaryElement.offsetHeight;

    return primaryElementHeight + adjacentSiblingElementHeight > rootHeight;
  }

  private get isPrimaryElementSticky(): boolean {
    const primaryElement: HTMLElement = this.primaryElement;
    if (!primaryElement) return;
    return primaryElement.style.position === 'fixed';
  }

  private get isPrimaryElementRelative(): boolean {
    const primaryElement: HTMLElement = this.primaryElement;
    if (!primaryElement) return;
    return primaryElement.style.position === 'relative';
  }

  private set primaryElementStyle(primaryElementStyleObj: IPrimaryElementStyle) {
    const primaryElement: HTMLElement = this.primaryElement;
    if (!primaryElement) return;
    const keys: string[] = Object.keys(primaryElementStyleObj);
    keys.forEach(key => primaryElement.style[key] = primaryElementStyleObj[key]);
  }
}
